import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoChevronRight,
  IcoSearch,
  IcoUser,
  IcoX,
} from "../../components/icons";
import {
  BrandedLayout,
  CommentButton,
  CommentThread,
  MemberHoverCard,
  MentionRichTextEditor,
  prefetchMembersForMention,
  ReactionBar,
} from "../../components/ui";
import { decodeMentionsToHtml } from "../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, HighFive, User } from "../../types";
import { HIGH_FIVE_PAGE_SIZE, isAdmin } from "../../types";
import { formatDateShort } from "../../utils/format";

type HighFivesPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  currentMemberId: string;
  activeMembers: { id: string; name: string }[];
  highFives: HighFive[];
  hasMore: boolean;
  initialRecipientId: string | null;
};

export async function load(args: LoaderArgs): Promise<HighFivesPageState> {
  const orgId = args.params.orgId ?? "";
  const initialRecipientId = args.searchParams.to ?? null;

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const currentUser = authResult.user as User;
  const currentMember = orgResult.currentMember;
  const isOrgAdmin = isAdmin(currentMember.role);

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Fetch members and feed in parallel (after auth validation)
  const [mentionMembers, feedResult] = await Promise.all([
    rpc.members.listForMention({ workspaceId: orgId }),
    rpc.highFives.list({
      workspaceId: orgId,
      limit: HIGH_FIVE_PAGE_SIZE,
    }),
  ]);

  const activeMembers = mentionMembers.map((m) => ({
    id: m.memberId,
    name: m.name,
  }));

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin,
    hasReports: orgFromList?.hasReports ?? false,
    currentMemberId: currentMember.id,
    activeMembers,
    highFives: feedResult.highFives,
    hasMore: feedResult.hasMore,
    initialRecipientId,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("High Fives");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
    currentMemberId,
    activeMembers,
  } = state.value;

  const highFives = useSignal<HighFive[]>(state.value.highFives);
  const hasMore = useSignal(state.value.hasMore);
  const loadingMore = useSignal(false);

  async function handleLoadMore() {
    if (loadingMore.value || !hasMore.value) return;
    loadingMore.value = true;
    try {
      const result = await rpc.highFives.list({
        workspaceId: orgId,
        limit: HIGH_FIVE_PAGE_SIZE,
        offset: highFives.value.length,
      });
      highFives.value = [...highFives.value, ...result.highFives];
      hasMore.value = result.hasMore;
    } catch {
      showError("Failed to load more high fives");
    } finally {
      loadingMore.value = false;
    }
  }

  function handleCreated(hf: HighFive) {
    highFives.value = [hf, ...highFives.value];
  }

  // Group high fives by date
  const grouped = groupByDate(highFives.value);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="high-fives"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex flex-col gap-6">
        <ComposeBox
          orgId={orgId}
          activeMembers={activeMembers}
          currentMemberId={currentMemberId}
          initialRecipientId={state.value.initialRecipientId}
          onCreated={handleCreated}
        />

        {grouped.length === 0 && (
          <div class="bg-surface border border-border rounded-lg p-8 text-center">
            <p class="text-text-muted">
              No high fives yet. Be the first to recognize someone!
            </p>
          </div>
        )}

        {grouped.map((group) => (
          <div key={group.label} class="flex flex-col gap-3">
            <h3 class="text-sm font-medium text-text-muted">{group.label}</h3>
            {group.items.map((hf) => (
              <HighFiveCard
                key={hf.id}
                hf={hf}
                orgId={orgId}
                currentMemberId={currentMemberId}
              />
            ))}
          </div>
        ))}

        {hasMore.value && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore.value}
            class="self-center px-4 py-2 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            {loadingMore.value ? "Loading…" : "Load More"}
          </button>
        )}
      </div>
    </BrandedLayout>
  );
}

// ============================================================================
// Compose Box
// ============================================================================

function ComposeBox({
  orgId,
  activeMembers,
  currentMemberId,
  initialRecipientId,
  onCreated,
}: {
  orgId: string;
  activeMembers: { id: string; name: string }[];
  currentMemberId: string;
  initialRecipientId: string | null;
  onCreated: (hf: HighFive) => void;
}) {
  // Pre-select recipient if ?to= param was provided and the member exists
  const validInitialId =
    initialRecipientId &&
    initialRecipientId !== currentMemberId &&
    activeMembers.some((m) => m.id === initialRecipientId)
      ? [initialRecipientId]
      : [];
  const selectedIds = useSignal<string[]>(validInitialId);
  // Sync signal when initialRecipientId changes (useSignal only uses value on mount)
  useEffect(() => {
    selectedIds.value = validInitialId;
  }, [initialRecipientId]);
  const message = useSignal("");
  const sending = useSignal(false);
  const search = useSignal("");
  const dropdownOpen = useSignal(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Prefetch members for @mention suggestions
  useEffect(() => {
    prefetchMembersForMention(orgId);
  }, [orgId]);

  // Filter out current user from recipients
  const availableMembers = activeMembers.filter(
    (m) => m.id !== currentMemberId,
  );

  const filteredMembers = availableMembers.filter((m) =>
    m.name.toLowerCase().includes(search.value.toLowerCase()),
  );

  const canSend =
    selectedIds.value.length > 0 &&
    message.value.trim().length > 0 &&
    !sending.value;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        dropdownOpen.value = false;
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function toggleRecipient(id: string) {
    if (selectedIds.value.includes(id)) {
      selectedIds.value = selectedIds.value.filter((x) => x !== id);
    } else {
      selectedIds.value = [...selectedIds.value, id];
    }
    // Clear search so full list is visible after selection
    search.value = "";
  }

  function removeRecipient(id: string) {
    selectedIds.value = selectedIds.value.filter((x) => x !== id);
  }

  async function handleSend() {
    if (!canSend) return;
    sending.value = true;
    try {
      await rpc.highFives.create({
        workspaceId: orgId,
        message: message.value.trim(),
        recipientMemberIds: selectedIds.value,
      });
      // Reload the feed to get the full high five with recipients
      const feedResult = await rpc.highFives.list({
        workspaceId: orgId,
        limit: 1,
      });
      if (feedResult.highFives[0]) {
        onCreated(feedResult.highFives[0]);
      }
      selectedIds.value = [];
      message.value = "";
      search.value = "";
      // Clean ?to= from URL so refresh doesn't re-select
      if (initialRecipientId) {
        const url = new URL(window.location.href);
        url.searchParams.delete("to");
        history.replaceState(null, "", url.pathname);
      }
    } catch {
      showError("Failed to send high five");
    } finally {
      sending.value = false;
    }
  }

  const selectedMembers = availableMembers.filter((m) =>
    selectedIds.value.includes(m.id),
  );

  return (
    <div class="bg-surface border border-border rounded-lg p-4 flex flex-col gap-3">
      {/* Recipient picker */}
      <div ref={dropdownRef} class="relative">
        <label class="text-sm font-medium text-text mb-1 block">To</label>

        {/* Selected pills */}
        {selectedMembers.length > 0 && (
          <div class="flex flex-wrap gap-1.5 mb-2">
            {selectedMembers.map((m) => (
              <span
                key={m.id}
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-muted text-sm text-text"
              >
                {m.name}
                <button
                  type="button"
                  onClick={() => removeRecipient(m.id)}
                  class="text-text-muted hover:text-text"
                >
                  <IcoX class="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search input */}
        <div class="relative">
          <IcoSearch class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search members…"
            value={search.value}
            onInput={(e) => {
              search.value = (e.target as HTMLInputElement).value;
            }}
            onFocus={() => {
              dropdownOpen.value = true;
            }}
            class="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]"
          />
        </div>

        {/* Dropdown list */}
        {dropdownOpen.value && (
          <div class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-surface border border-border rounded-md shadow-lg">
            {filteredMembers.length === 0 ? (
              <div class="px-3 py-2 text-sm text-text-muted">
                No members found
              </div>
            ) : (
              filteredMembers.map((m) => {
                const checked = selectedIds.value.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleRecipient(m.id)}
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-hover transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      class="rounded border-border"
                    />
                    <span class="text-text">{m.name}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Message editor with @mention support */}
      <div class="w-full border border-border rounded-md bg-surface focus-within:ring-2 focus-within:ring-[var(--brand-500)] px-3 py-2 text-sm [&_.tiptap]:min-h-[3.5em] [&_.tiptap]:outline-none">
        <MentionRichTextEditor
          value={message.value}
          onChange={(val) => {
            message.value = val;
          }}
          workspaceId={orgId}
          placeholder="Write your high five message…"
        />
      </div>

      {/* Send button */}
      <div class="flex justify-start">
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          class="px-4 py-2 text-sm font-medium text-white bg-[var(--brand-600)] rounded-lg hover:bg-[var(--brand-700)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending.value ? "Sending…" : "Send High Five"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// High Five Card
// ============================================================================

function HighFiveCard({
  hf,
  orgId,
  currentMemberId,
}: {
  hf: HighFive;
  orgId: string;
  currentMemberId: string;
}) {
  const commentsOpen = useSignal(false);
  const commentCount = useSignal(0);

  return (
    <div class="bg-surface border border-border rounded-lg p-4 flex flex-col gap-3">
      <div class="flex items-start gap-3">
        {/* Author avatar */}
        <MemberHoverCard
          memberId={hf.author.memberId}
          workspaceId={orgId}
          class="flex-shrink-0"
        >
          <div class="cursor-pointer">
            {hf.author.profilePictureUrl ? (
              <img
                src={hf.author.profilePictureUrl}
                alt={hf.author.name}
                class="size-9 rounded-full object-cover"
              />
            ) : (
              <div class="size-9 rounded-full flex items-center justify-center bg-surface-muted">
                <IcoUser class="size-5 text-text-muted" />
              </div>
            )}
          </div>
        </MemberHoverCard>

        <div class="flex flex-col min-w-0 flex-1 gap-1">
          <div class="flex items-center gap-1.5 text-sm flex-wrap">
            <MemberHoverCard memberId={hf.author.memberId} workspaceId={orgId}>
              <a
                href={`/orgs/${orgId}/members/${hf.author.memberId}/profile`}
                class="font-semibold text-text cursor-pointer hover:underline"
              >
                {hf.author.name}
              </a>
            </MemberHoverCard>
            <IcoChevronRight class="size-3.5 text-text-subtle flex-shrink-0" />
            {hf.recipients.map((r, i) => (
              <span key={r.memberId}>
                {i > 0 && <span class="text-text-subtle">,</span>}{" "}
                <MemberHoverCard memberId={r.memberId} workspaceId={orgId}>
                  <a
                    href={`/orgs/${orgId}/members/${r.memberId}/profile`}
                    class="font-semibold text-text cursor-pointer hover:underline"
                  >
                    {r.name}
                  </a>
                </MemberHoverCard>
              </span>
            ))}
          </div>
          <div
            class="text-sm text-text-muted [&_.mention]:text-[var(--brand-600)] [&_.mention]:font-medium [&_p]:m-0 [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: decodeMentionsToHtml(hf.message),
            }}
          />
        </div>
      </div>

      {/* Date, reactions, and comments */}
      <div class="flex items-center gap-2 ml-12">
        <span class="text-xs text-text-subtle">
          {formatDateShort(hf.createdAt)}
        </span>
        <div class="flex-1" />
        <ReactionBar
          workspaceId={orgId}
          targetType="high_five"
          targetId={hf.id}
        />
        <CommentButton
          count={commentCount.value}
          isOpen={commentsOpen.value}
          onClick={() => {
            commentsOpen.value = !commentsOpen.value;
          }}
        />
      </div>

      {commentsOpen.value && (
        <div class="ml-12">
          <CommentThread
            workspaceId={orgId}
            targetType="high_five"
            targetId={hf.id}
            currentMemberId={currentMemberId}
            onCommentCountChange={(count) => {
              commentCount.value = count;
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function groupByDate(
  highFives: HighFive[],
): { label: string; items: HighFive[] }[] {
  const groups: Map<string, HighFive[]> = new Map();

  for (const hf of highFives) {
    const date = new Date(hf.createdAt);
    const label = formatDateShort(date);
    const existing = groups.get(label);
    if (existing) {
      existing.push(hf);
    } else {
      groups.set(label, [hf]);
    }
  }

  return Array.from(groups.entries()).map(([label, items]) => ({
    label,
    items,
  }));
}
